import React, { useState, useEffect } from "react";
import { PageBanner, BigHeading, Tickertape } from "../components";
import { tw } from "../tw";
import { graphql } from "gatsby";
import { Helmet } from "react-helmet";
import { GatsbyImage } from "gatsby-plugin-image";
import { useSpring, animated } from "react-spring";
import BlockContent from "@sanity/block-content-to-react";

/* Sponsors Page
============================================================================= */

const SponsorsPage = ({ data }) => {
  return (
    <>
      <Helmet>
        <title>Sponsors</title>
      </Helmet>
      <PageBanner />
      <Tickertape />
      <BigHeading text="Sponsors" />
      <SponsorsSection data={data} />
      <div className="h-8 mx-auto" />
      <div className={tw.blockContentContainer}>
        <div className="prose-xl block-content">
          If your business or brand is interested in becoming part of Good
          Things festival, please email{" "}
          <a
            href="mailto:info@goodthingsfestival.com.au"
            className="font-bold underline"
          >
            info@goodthingsfestival.com.au
          </a>
        </div>
      </div>
    </>
  );
};

/* Sponsors Section
============================================================================= */

const SponsorsSection = ({ data }) => {
  const sponsors = data.allSanitySponsors.nodes;

  const renderSponsors = () => {
    return sponsors.map((sponsor, i) => {
      return (
        <SponsorCard
          key={i}
          name={sponsor.title}
          copy={sponsor._rawContent}
          fluid={sponsor.logo.asset.gatsbyImageData}
        />
      );
    });
  };

  return <div className="sponsors-container">{renderSponsors()}</div>;
};

/* Sponsor Card
============================================================================= */

const SponsorCard = ({ name = "SPONSOR", copy = "", fluid, bg = "white" }) => {
  const [flipped, set] = useState(false);
  const [copyShow, setCopyShow] = useState(false);
  const { transform, opacity } = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(800px) rotateX(${flipped ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });

  useEffect(() => {
    setTimeout(() => {
      setCopyShow(true);
    }, 500);
  }, []);

  return (
    <div
      className="sponsor-card-container"
      onClick={() => set((state) => !state)}
    >
      <animated.div
        className="c sponsor-card bg-white"
        style={{
          opacity: opacity.interpolate((o) => 1 - o),
          transform,
          padding: "25px",
          backgroundColor: "white",
        }}
      >
        <GatsbyImage
          image={fluid}
          objectFit="cover"
          className="bg-white"
          style={{
            height: "100%",
            width: "100%",
            borderRadius: "15px",
          }}
          imgStyle={{
            objectFit: "contain",
            objectPosition: "50% 50%",
            pointerEvents: "none",
          }}
        />
      </animated.div>
      <animated.div
        className="c sponsor-card-backside"
        style={{
          opacity,
          transform: transform.interpolate((t) => `${t} rotateX(180deg)`),
        }}
      >
        <h2 className="sponsor-heading uppercase text-xl pt-3 pb-2">{name}</h2>
        <p className="sponsor-copy">
          {copyShow && (
            <BlockContent
              blocks={copy}
              className="block-content prose-lg text-base"
              renderContainerOnSingleChild
            />
          )}
        </p>
      </animated.div>
    </div>
  );
};

/* Page Query
============================================================================= */

export const pageQuery = graphql`
  query SponsorsPageQuery {
    allSanitySponsors {
      nodes {
        title
        logo {
          asset {
            gatsbyImageData(
              width: 512
              placeholder: BLURRED
              formats: [AUTO, WEBP, AVIF]
            )
          }
        }
        _rawContent(resolveReferences: { maxDepth: 10 })
      }
    }
  }
`;

/* Export
============================================================================= */

export default SponsorsPage;
